import {
  Component,
  Inject,
  Input,
  OnChanges,
  PLATFORM_ID,
  SimpleChanges,
} from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { IconComponent } from '../../atoms/icon/icon.component';
import { BadgeComponent } from '../../atoms/badge/badge.component';
import { ModalComponent } from '../modal/modal.component';
import {
  UtilitiesCards,
  UtilitiesCardsItem,
} from '../../../shared/interfaces/utilities-cards.interface';
import { Colors, Headlines, Sizes } from '../../../shared/enums/shared.enums';
import BadgeType from '../../../shared/enums/badge-type.enums';
import { TooltipComponent } from '../tooltip/tooltip.component';
import { Tooltip } from '../../../shared/interfaces/tooltip.interface';
import { CaasComponent } from '../../../shared/interfaces/caas/caas.interface';
import { Router, RouterModule } from '@angular/router';
import { CaasRoutesService } from '../../../services/caas-routes.service';
import { CaasBodytextService } from '../../../services/caas-bodytext.service';
import { CapitalizePipe } from '../../capitalize.pipe';
import { safeAssign } from '../../../shared/utils/safe-assign.util';
import { ErrorHandlerService } from '../../../services/error-handler.service';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { SwiperModule } from 'swiper/angular';

@Component({
  selector: 'hdi-utilities-cards',
  standalone: true,
  imports: [
    CommonModule,
    IconComponent,
    BadgeComponent,
    ModalComponent,
    TooltipComponent,
    RouterModule,
    CapitalizePipe,
    NgxSkeletonLoaderModule,
    SwiperModule,
  ],
  templateUrl: './utilities-cards.component.html',
  styleUrls: ['./utilities-cards.component.scss'],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class UtilitiesCardsComponent implements OnChanges {
  @Input() utilitiesCards: UtilitiesCards;
  @Input() columns: 'one-column' | 'two-columns' | 'three-columns' =
    'two-columns';
  @Input() headlineSize: Headlines = Headlines.H4;
  @Input() backgroundColor: 'white' | 'grey' = 'white';
  @Input() caasData?: CaasComponent;
  @Input() sectionBg?: 'white' | 'grey';

  Sizes = Sizes;
  Colors = Colors;
  BadgeType = BadgeType;
  Headlines = Headlines;

  showModal = false;
  modal: { title: string; description: string };
  tooltip: Tooltip;
  public isLoading = true;

  constructor(
    private readonly caasRoutesService: CaasRoutesService,
    private readonly caasBodytextService: CaasBodytextService,
    private readonly router: Router,
    private readonly errorHandler: ErrorHandlerService,
    @Inject(PLATFORM_ID) private readonly platformId: object
  ) {
    this.utilitiesCards = {
      title: '',
      key: '',
      items: [],
    };
    this.modal = {
      title: '',
      description: '',
    };
    this.tooltip = {
      id: '',
      description: '',
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['caasData']) {
      this.utilitiesCards = this.setCaasUtilitiesCards();
      this.backgroundColor = this.sectionBg === 'grey' ? 'grey' : 'white';
      this.columns = this.getCaasColumns();
    }
    if (isPlatformBrowser(this.platformId)) {
      this.isLoading = false;
    }
  }

  openModal(modal: { title: string; description: string }) {
    this.modal = {
      ...modal,
      description: this.caasBodytextService.extractTextEditorContent(
        modal.description
      ),
    };
    this.showModal = !this.showModal;
  }

  openTooltip(tooltip: Tooltip, event: Event) {
    event.stopPropagation();
    this.tooltip = tooltip;
  }

  closeTooltip() {
    this.tooltip = { id: '', description: '' };
  }

  isArray(value: string | string[]): value is string[] {
    return Array.isArray(value);
  }

  getCaasColumns() {
    if (!this.caasData?.formData?.st_columns?.value) {
      return 'two-columns';
    }
    switch (this.caasData.formData.st_columns.value.identifier) {
      case '3':
        return 'three-columns';
      case '2':
        return 'two-columns';
      default:
        return 'two-columns';
    }
  }

  setCaasUtilitiesCards() {
    let title: string = '';
    let key: string = '';
    let items: UtilitiesCardsItem[] = [];
    safeAssign(
      () => {
        title = this.caasData?.formData?.st_header?.value ?? '';
      },
      'Assigning title',
      this.errorHandler
    );
    safeAssign(
      () => {
        key = this.caasData?.identifier ?? '';
      },
      'Assigning key',
      this.errorHandler
    );
    safeAssign(
      () => {
        items = this.getCaasUtilitiesCardsItems();
      },
      'Assigning items',
      this.errorHandler
    );
    return {
      title: title,
      key: key,
      items: items,
    };
  }

  getCaasUtilitiesCardsItems() {
    if (this.caasData?.formData?.st_items?.value?.length) {
      return this.caasData.formData.st_items.value
        .map((item) => {
          let hasTooltip;
          let linkType = '';
          let label = '';
          let badgeLabel = '';
          let tooltip: { id: string; description: string } | undefined;
          let url = '';
          let title = '';
          if (item?.formData?.st_tooltip?.value) {
            hasTooltip = true;
          } else {
            hasTooltip = false;
          }
          safeAssign(
            () => {
              linkType =
                item?.formData?.st_link?.value?.formData?.lt_link_type?.value
                  ?.identifier ?? '';
            },
            'Assigning linkType',
            this.errorHandler
          );
          safeAssign(
            () => {
              label =
                item?.formData?.st_link?.value?.formData?.lt_link_label
                  ?.value ?? '';
            },
            'Assigning label',
            this.errorHandler
          );
          safeAssign(
            () => {
              badgeLabel = item?.formData?.st_badge?.value ?? '';
            },
            'Assigning badgeLabel',
            this.errorHandler
          );
          safeAssign(
            () => {
              tooltip = hasTooltip
                ? {
                    id: item.identifier,
                    description: item?.formData?.st_tooltip?.value ?? '',
                  }
                : undefined;
            },
            'Assigning tooltip',
            this.errorHandler
          );
          safeAssign(
            () => {
              url =
                linkType === 'internal'
                  ? (this.caasRoutesService.getSeoRouteByIdentifier(
                      item?.formData?.st_link?.value?.formData?.lt_link_internal
                        ?.value?.identifier ?? '',
                      this.router.config
                    )?.path ?? '404')
                  : (item?.formData?.st_link?.value?.formData?.lt_link_external
                      ?.value ?? '');
            },
            'Assigning url',
            this.errorHandler
          );
          safeAssign(
            () => {
              title =
                item?.formData?.st_link?.value?.formData?.lt_link_title
                  ?.value ?? '';
            },
            'Assigning title',
            this.errorHandler
          );
          const itemData = {
            label: label,
            badgeLabel: badgeLabel,
            tooltip: tooltip,
            rightIcon: hasTooltip ? 'circle-info' : undefined,
            target: linkType === 'external' ? '_blank' : '_self',
            url: url,
            title: title,
          };
          return itemData;
        })
        .filter((item) => item !== undefined);
    } else {
      return [];
    }
  }

  navigateToInternalPage(event: Event, url: string) {
    event.preventDefault();
    this.router.navigate([url]);
  }
}
